import { useEffect, useState } from "react";
import { Link } from "react-router";
import request from "@/api";
import {
    ProductDetailTabSection,
    ProductDetailTabTable,
    ProductDetailTabMetrics,
    emptyProductDetailColumns,
    withProductDetailDateRange,
    type ProductDetailFilterValues,
    type ProductDetailTabProps
} from "@/views/pages/productManagement/ProductDetailTabPrimitives";

export type orderInfoProps = ProductDetailTabProps;

const ORDER_STATE_LABELS: Record<number, string> = { 3: "提货中", 4: "已完成", 5: "已取消", 6: "待确认支付" };
const mobileText = (value: unknown) => {
    const text = String(value || "");
    return text ? text.replace(/(\d{3})(\d{4})(\d{4})/, "$1 $2 $3") : "";
};
const numberText = (value: unknown, money = false) => {
    if (value === null || value === undefined || value === "") return "--";
    const number = Number(value);
    return Number.isFinite(number)
        ? number.toLocaleString("en-US", money ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : undefined)
        : String(value);
};
const dateText = (value: unknown) =>
    String(value || "--")
        .replace("T", " ")
        .slice(0, 16);

/** goodsManagement / consignment / components / orderInfo：保留数据展示、编辑提交和父级事件回调。 */
export default function MigrationComponent(props: orderInfoProps) {
    const productId = String(props.data?.productConsignment?.id || props.data?.id || "");
    const [ton, setTon] = useState<{ orderedTon: number; pickedUpTon: number }>({ orderedTon: 0, pickedUpTon: 0 });
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState<ProductDetailFilterValues>({});

    useEffect(() => {
        let disposed = false;
        if (!productId) return;
        // 与旧 Vue orderInfo 保持相同的 productType=1 和分页参数。
        void Promise.all([
            request.service.get(`/admin/order/ton`, { params: { productId, productType: 1 } }),
            request.service.get(`/admin/order/paging`, {
                params: {
                    page: 1,
                    size: 10,
                    productId,
                    productType: 1,
                    ...withProductDetailDateRange(filters, "date")
                }
            })
        ])
            .then(([tonResponse, pageResponse]) => {
                if (disposed) return;
                setTon(tonResponse.data || { orderedTon: 0, pickedUpTon: 0 });
                const list = pageResponse.data?.list || [];
                setTotal(Number(pageResponse.data?.total || list.length));
                setRows(
                    list.map((item: any, index: number) => ({
                        id: item.order?.id || item.id || `order-${index}`,
                        订单编号: item.order?.id ? (
                            <Link to={`/tradeManage/tradeOrder/detail?id=${item.order.id}`}>
                                {item.order.orderId || item.order.id}
                            </Link>
                        ) : (
                            "--"
                        ),
                        采购用户:
                            [
                                item.order?.customerEnterprise?.name || item.order?.customerEnterprise?.realName,
                                mobileText(item.order?.customerEnterprise?.mobile)
                            ]
                                .filter(Boolean)
                                .join(" | ") || "--",
                        "交易单价(元/吨)": numberText(item.order?.unitPrice),
                        "采购吨数(吨)": numberText(item.order?.ton),
                        "预估金额(元)": numberText(item.order?.estimatedAmount, true),
                        "实收款(元)": numberText(item.order?.actualReceivedAmount ?? item.paidAmount, true),
                        下单时间: dateText(item.order?.orderTime || item.order?.createdTime),
                        状态: ORDER_STATE_LABELS[Number(item.order?.state)] || item.order?.stateName || "--"
                    }))
                );
            })
            .catch(() => {
                if (!disposed) setRows([]);
            });
        return () => {
            disposed = true;
        };
    }, [productId, filters]);

    return (
        <ProductDetailTabSection title="订单明细" className="order-info" showTitle={false}>
            <ProductDetailTabMetrics
                items={[
                    { label: "已售吨数（吨）", value: ton.orderedTon.toLocaleString(), type: "num" },
                    { label: "已提货吨数（吨）", value: ton.pickedUpTon.toLocaleString(), type: "num" }
                ]}
            />
            <ProductDetailTabTable
                ariaLabel="订单明细"
                columns={emptyProductDetailColumns([
                    "订单编号",
                    "采购用户",
                    "交易单价(元/吨)",
                    "采购吨数(吨)",
                    "预估金额(元)",
                    "实收款(元)",
                    "下单时间",
                    "状态"
                ])}
                data={rows}
                total={total}
                onSearch={setFilters}
            />
            {props.children}
        </ProductDetailTabSection>
    );
}
